import { useStore } from '@tanstack/react-form'
import { LakehouseTableInput, LakehouseTableStatusEnum } from '@vendia/management-api-types'
import { FetchLakehouseDataDocument } from '@vendia/control-plane-api'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import Card from 'src/components/containers/card'
import CheckboxGroupField from 'src/components/fields/checkbox-group.field'
import { ScrollableStepContent } from 'src/components/flows/scrollable-step-content'
import StepButtons from 'src/components/flows/step-buttons'
import { StepContentHeader } from 'src/components/flows/step-header'
import { StepWrapper } from 'src/components/flows/step-wrapper'
import { StepComponent } from 'src/components/flows/types'
import { StepId } from 'src/pages/uni-create/config'
import { isRequiredValidator } from 'src/utils/form/validation'
import useControlPlaneApi from 'src/utils/hooks/use-control-plane-api'
import { LakehouseFlowStepValues } from 'src/utils/lakehouse/types'
import { lakehouseFlowFieldValidator } from 'src/utils/lakehouse/validators'
import { getNodeDbName } from 'src/utils/sql/query'

export const StepSelectVendiaTables: StepComponent<LakehouseFlowStepValues> = ({ context }) => {
  const { existingVendiaTables, product, nodeName, uniName } = useStore(context.form.store, (state) => state.values)
  const client = useControlPlaneApi()
  const [isFetchingTableInfo, setIsFetchingTableInfo] = useState(false)
  const navigate = useNavigate()

  const buildDescribeQuery = (table: LakehouseTableInput) => {
    return `SELECT
      column_name,
      data_type
    FROM
      information_schema.columns
    WHERE
      table_name = '${table.name}'`
  }

  return (
    <StepWrapper>
      <StepContentHeader
        centered
        large
        title='Select Vendia tables'
        description={'Select one or more Vendia tables as a source for the new table'}
      />
      <ScrollableStepContent inset autoHeight>
        <Card padding='xl' className='mx-8 w-full max-w-4xl' title='Tables'>
          <CheckboxGroupField
            name='product.selectedVendiaTables'
            options={
              existingVendiaTables
                ? existingVendiaTables
                    .filter((table) => table.status === LakehouseTableStatusEnum.Ready || table.status === undefined)
                    .map((table) => ({ label: table.name, value: table }))
                : []
            }
            validators={lakehouseFlowFieldValidator({
              stepId: StepId.SelectVendiaTables,
              validator: isRequiredValidator(),
            })}
            form={context.form}
            wrapperClassName='!mb-0'
          />
        </Card>
      </ScrollableStepContent>
      <context.form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting, state.isPristine]}>
        {([canSubmit, isSubmitting, isPristine]) => (
          <StepButtons
            context={context}
            nextBlocked={!canSubmit || isPristine}
            isSubmitting={isFetchingTableInfo}
            hasCancel={true}
            onCancel={() => navigate('../', { relative: 'path' })}
            onNext={async () => {
              let validation = await context.validateFlowState()
              if (!validation.isValid) {
                return
              }
              setIsFetchingTableInfo(true)
              if (product.selectedVendiaTables?.length === 1) {
                const selectedTable = product.selectedVendiaTables[0]
                context.form.setFieldValue(
                  'selectedTableColumns',
                  selectedTable.columns.map((col) => ({
                    name: col.name,
                    type: col.fieldType,
                    nullable: col.required,
                    primaryKey: col.identifier,
                    uniqueKey: col.identifier,
                  })),
                )
                context.form.setFieldValue(
                  'source.selectedTable',
                  //@ts-ignore // Table def needs to be updated
                  `${getNodeDbName(uniName!, selectedTable._owner!)}.${selectedTable.name}`,
                )
              } else {
                const selectedJoinTables = []
                for (const table of (product.selectedVendiaTables ?? []) as LakehouseTableInput[]) {
                  const query = buildDescribeQuery(table)
                  const response = await client.graphqlFetch({ query: FetchLakehouseDataDocument, variables: {
                    uniName: uniName!,
                    nodeName: nodeName!,
                    sqlText: query,
                  } })
                  selectedJoinTables.push({
                    tableName: table.name,
                    columnsInfo: response?.fetchLakehouseData?.results,
                  })
                }
                context.form.setFieldValue('joinTables', selectedJoinTables)
              }
              setIsFetchingTableInfo(false)

              if (product.selectedVendiaTables && product.selectedVendiaTables.length > 1) {
                context.goToStep(StepId.JoinVendiaTables)
              } else {
                context.goToStep(StepId.TableSettings)
              }
            }}
          />
        )}
      </context.form.Subscribe>
    </StepWrapper>
  )
}
